import React from 'react';
import {
  Box,
  Skeleton,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';

interface SkeletonLoaderProps {
  type: 'chat' | 'workflow' | 'form';
  count?: number;
}

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({ type, count = 3 }) => {
  const renderChatSkeleton = () => (
    <List>
      {Array.from({ length: count }).map((_, index) => (
        <React.Fragment key={index}>
          <ListItem sx={{ px: 0 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Skeleton variant="circular" width={32} height={32} />
            </ListItemIcon>
            <ListItemText
              primary={<Skeleton variant="text" width="60%" height={20} />}
              secondary={<Skeleton variant="text" width="40%" height={16} />}
            />
          </ListItem>
          {index < count - 1 && <Divider />}
        </React.Fragment>
      ))}
    </List>
  );

  const renderWorkflowSkeleton = () => (
    <Box>
      {Array.from({ length: count }).map((_, index) => (
        <Card key={index} sx={{ mb: 2, p: 2 }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Skeleton variant="text" width="70%" height={24} />
              <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
            </Box>
            <Skeleton variant="text" width="50%" height={16} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={16} sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" width={80} height={20} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" width={100} height={20} sx={{ borderRadius: 1 }} />
            </Box>
            <Skeleton variant="rectangular" width="100%" height={6} sx={{ borderRadius: 3 }} />
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  const renderFormSkeleton = () => (
    <Box>
      {Array.from({ length: count }).map((_, index) => (
        <Card key={index} sx={{ mb: 2, p: 2 }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton variant="rectangular" width={100} height={24} sx={{ borderRadius: 1 }} />
            </Box>
            <Skeleton variant="text" width="80%" height={16} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={16} sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Skeleton variant="rectangular" width={80} height={20} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" width={90} height={20} sx={{ borderRadius: 1 }} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Skeleton variant="text" width="50%" height={14} />
              <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1 }} />
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  switch (type) {
    case 'chat':
      return renderChatSkeleton();
    case 'workflow':
      return renderWorkflowSkeleton();
    case 'form':
      return renderFormSkeleton();
    default:
      return null;
  }
};

export default SkeletonLoader;
